/**
 * DrHint Embed Registry — one entry per "full page" embed widget.
 * Each embed renders a complete ready-made page inside the publisher's site
 * (chart + live prices + calculator + intent CTAs) and ships with a visible
 * do-follow credit anchor in the paste snippet.
 */
export type EmbedCategory = "gold" | "silver" | "calculator";

export type EmbedDef = {
  id: string;
  name: string;
  category: EmbedCategory;
  /** Human blurb shown on the hub */
  blurb: string;
  /** Page on drhint.com the embed sends traffic to (credit anchor target) */
  target: string;
  /** Anchor text used in the visible do-follow credit line */
  anchor: string;
  /** Initial iframe height before auto-resize kicks in */
  height: number;
};

export const EMBEDS: EmbedDef[] = [
  {
    id: "gold-pakistan",
    name: "Gold Rate in Pakistan — Full Page",
    category: "gold",
    blurb:
      "Live 24K/22K/21K gold rate in PKR with 6-month chart, tola & gram breakdown, mini calculator and Urdu city CTA.",
    target: "/pakistan",
    anchor: "Gold Rate in Pakistan Today",
    height: 1500,
  },
  {
    id: "gold-india",
    name: "Gold Rate in India — Full Page",
    category: "gold",
    blurb:
      "Live 24K/22K/18K gold price per 10 gram in INR with chart, karat table, mini calculator and city CTA.",
    target: "/india",
    anchor: "Gold Rate in India Today",
    height: 1500,
  },
  {
    id: "silver-pakistan",
    name: "Silver Rate in Pakistan — Full Page",
    category: "silver",
    blurb: "Live silver per tola, gram, 10 gram and kg in PKR with converter and city intent links.",
    target: "/silver-rate-pakistan",
    anchor: "Silver Rate in Pakistan Today",
    height: 1350,
  },
  {
    id: "silver-india",
    name: "Silver Rate in India — Full Page",
    category: "silver",
    blurb: "Live silver per gram, 10 gram and kg in INR with converter and city intent links.",
    target: "/silver-rate-india",
    anchor: "Silver Rate in India Today",
    height: 1350,
  },
  {
    id: "gold-calculator-pakistan",
    name: "Today Gold Rate Calculator (Pakistan)",
    category: "calculator",
    blurb: "Weight × karat gold value calculator on live PKR rates, with jump to the full Pakistan calculator.",
    target: "/today-gold-rate-calculator-pakistan",
    anchor: "Today Gold Rate Calculator Pakistan",
    height: 1050,
  },
  {
    id: "gold-calculator-india",
    name: "Today Gold Rate Calculator (India)",
    category: "calculator",
    blurb: "Weight × karat gold value calculator on live INR rates, with jump to the full India calculator.",
    target: "/today-gold-rate-calculator-india",
    anchor: "Today Gold Rate Calculator India",
    height: 1050,
  },
  {
    id: "age-calculator",
    name: "Age Calculator — Full Page",
    category: "calculator",
    blurb: "Exact age in years, months, days, hours plus next-birthday countdown.",
    target: "/age-calculator",
    anchor: "Age Calculator",
    height: 1000,
  },
  {
    id: "pregnancy-calculator",
    name: "Pregnancy Due Date Calculator",
    category: "calculator",
    blurb: "LMP based due date, gestational age and trimester with week-by-week intent links.",
    target: "/age-calculator/gestational-pregnancy",
    anchor: "Pregnancy & Gestational Age Calculator",
    height: 1000,
  },
  {
    id: "bmi-calculator",
    name: "BMI Calculator — Full Page",
    category: "calculator",
    blurb: "Metric + imperial BMI with category band and healthy weight range.",
    target: "/bmi-calculator",
    anchor: "BMI Calculator",
    height: 1000,
  },
  {
    id: "zakat-calculator",
    name: "Zakat Calculator (Gold & Silver)",
    category: "calculator",
    blurb: "2.5% zakat on gold, silver, cash and savings using live metal rates.",
    target: "/zakat-calculator",
    anchor: "Zakat Calculator",
    height: 1050,
  },
];

export function findEmbed(id: string): EmbedDef | undefined {
  return EMBEDS.find((e) => e.id === id);
}

export const SITE = "https://drhint.com";

/** Universal one-paste snippet (auto-resizing, works everywhere). */
export function snippetFor(e: EmbedDef): string {
  return [
    `<div data-drhint-embed="${e.id}" style="min-height:${e.height}px"></div>`,
    `<p style="font-size:12px;font-family:Arial,sans-serif;margin:6px 0 0">Live data source: <a href="${SITE}${e.target}" target="_blank">${e.anchor}</a> by DrHint</p>`,
    `<script src="${SITE}/embed.js" async></script>`,
  ].join("\n");
}

/** Plain iframe fallback (Blogger / strict CMS that strip <script>). */
export function iframeSnippetFor(e: EmbedDef): string {
  return [
    `<iframe src="${SITE}/embed/page/${e.id}" width="100%" height="${e.height}" style="border:0;max-width:100%" loading="lazy" title="${e.name} by DrHint"></iframe>`,
    `<p style="font-size:12px;font-family:Arial,sans-serif;margin:6px 0 0">Live data source: <a href="${SITE}${e.target}" target="_blank">${e.anchor}</a> by DrHint</p>`,
  ].join("\n");
}

/** React / Next.js / Lovable component snippet. */
export function reactSnippetFor(e: EmbedDef): string {
  return `export default function DrHintEmbed() {
  return (
    <div>
      <iframe
        src="${SITE}/embed/page/${e.id}"
        style={{ width: "100%", height: ${e.height}, border: 0 }}
        loading="lazy"
        title="${e.name} by DrHint"
      />
      <p style={{ fontSize: 12 }}>
        Live data source:{" "}
        <a href="${SITE}${e.target}" target="_blank" rel="noopener">${e.anchor}</a> by DrHint
      </p>
    </div>
  );
}`;
}

/** JSON API snippet for developers who want raw numbers. */
export const API_SNIPPET = `fetch("${SITE}/api/public/hooks/gold-rates")
  .then((r) => r.json())
  .then((d) => {
    console.log(d.perTola24kPkr, d.per10gram24kInr);
  });`;

/**
 * Copy-paste prompt for AI website builders (Lovable, v0/Vercel, Bolt, Cursor…).
 * It tells the AI exactly which file to create, which snippet to paste and that
 * the credit anchor must stay visible — so the resulting page ships our backlink.
 */
export function aiPromptFor(e: EmbedDef): string {
  return `Create a new page on my website that embeds the free DrHint "${e.name}" widget.

Requirements:
1. Add a new route/page at a sensible URL (for example /${e.id}) with an SEO title, meta description and a single H1 about "${e.anchor}".
2. Inside the page body, paste this embed code EXACTLY as-is (do not modify the URLs, do not proxy them, do not rebuild the widget yourself):

<div data-drhint-embed="${e.id}" style="min-height:${e.height}px"></div>
<p style="font-size:12px;font-family:Arial,sans-serif;margin:6px 0 0">Live data source: <a href="${SITE}${e.target}" target="_blank">${e.anchor}</a> by DrHint</p>
<script src="${SITE}/embed.js" async></script>

   - If the framework blocks raw <script> tags (React/Next.js/Lovable), instead render:
     <iframe src="${SITE}/embed/page/${e.id}" style="width:100%;height:${e.height}px;border:0" loading="lazy" title="${e.name} by DrHint"></iframe>
     followed by the same visible credit paragraph with the normal (do-follow, no rel="nofollow") link to ${SITE}${e.target}.
3. Keep the credit link visible and do-follow — it is the licence condition for using this free widget.
4. Around the widget, write 200-300 words of my own intro text explaining what the page shows, plus an FAQ of 3 questions, so the page is useful and indexable.
5. Make it fully responsive; the widget must be full width on mobile.
6. Add the new page to my sitemap and link it from my main navigation.

The widget is live data and refreshes automatically — do not cache or hardcode any prices.`;
}
